import React, { ReactNode, useState, ReactElement, useEffect, useRef } from 'react';
import { useAccordionContext, AccordionProvider, Icon } from '@ver-uds/uswds-react';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { HeaderNavContext, NavItemsProps } from '../NavAccordion/NavItems';
import { NavItems, NavAccordion } from '../NavAccordion';

export interface GlobalHeaderProps {
  logo?: ReactNode;
  navItems?: ReactElement<NavItemsProps>;
  rightNavItems?: ReactElement<NavItemsProps>;
}

const primaryNavId = 'ver-primary-nav';

const HeaderNav = ({
  children,
  showMobileMenu,
  closeMobileMenu,
}: {
  children: ReactNode;
  showMobileMenu: boolean;
  closeMobileMenu: VoidFunction;
}): JSX.Element => {
  const { closeAll } = useAccordionContext();

  // Clicking on any link in the menu closes open accordions
  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    const { tagName } = event.target as Element;
    if (['a'].includes(tagName.toLowerCase())) {
      closeAll();
      closeMobileMenu();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- `onClick` is used here to capture the bubbled 'click' event from descendent elements.
    <nav
      id={primaryNavId}
      aria-label="Primary navigation"
      className={classNames('ver-nav', {
        'ver-nav--open': showMobileMenu,
      })}
      onClick={handleClick}
    >
      {children}
    </nav>
  );
};

const GlobalHeader = ({ logo = null, navItems, rightNavItems }: GlobalHeaderProps): JSX.Element => {
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuCloseRef = useRef<HTMLButtonElement | null>(null);
  const hasNavItems = Boolean(navItems || rightNavItems);

  useEffect(() => {
    const resize = (): void => {
      if (menuButtonRef.current && menuButtonRef.current.getBoundingClientRect().width === 0) {
        setShowMenu(false);
      }
    };
    window.addEventListener('resize', resize, false);
    return (): void => window.removeEventListener('resize', resize, false);
  }, []);

  useEffect(() => {
    if (showMenu) {
      menuCloseRef.current?.focus();
      document.body.classList.add('usa-js-mobile-nav--active');
    } else {
      menuButtonRef.current?.focus();
      document.body.classList.remove('usa-js-mobile-nav--active');
    }

    return () => {
      document.body.classList.remove('usa-js-mobile-nav--active');
    };
  }, [showMenu]);

  const MenuNav = (): JSX.Element => {
    const Menu = () => {
      return (
        <>
          <button
            ref={menuCloseRef}
            type="button"
            className="ver-navbar__menu ver-navbar__menu--close"
            aria-label="Close menu"
            aria-expanded={showMenu}
            onClick={() => {
              setShowMenu(false);
            }}
          >
            <Icon name="close" />
          </button>
          <HeaderNavContext.Provider value="left">{navItems}</HeaderNavContext.Provider>
          <div className="ver-nav__spacer" />
          <HeaderNavContext.Provider value="right">{rightNavItems}</HeaderNavContext.Provider>
        </>
      );
    };
    if (showMenu) {
      return (
        <FocusTrap>
          <div>
            <Menu />
          </div>
        </FocusTrap>
      );
    }
    return <Menu />;
  };

  return (
    <>
      <header className="ver-header">
        <div className="ver-nav-container">
          <div className="ver-navbar">
            <div className="ver-navbar__logo">{logo}</div>
            {hasNavItems ? (
              <button
                ref={menuButtonRef}
                type="button"
                className="ver-navbar__menu ver-navbar__menu--open"
                aria-label="Open menu"
                aria-expanded={showMenu}
                aria-controls={primaryNavId}
                onClick={() => {
                  setShowMenu(true);
                }}
              >
                <span>Menu</span>
                <Icon name="menu" />
              </button>
            ) : null}
          </div>
          {hasNavItems ? (
            <AccordionProvider autoClose>
              <HeaderNav
                showMobileMenu={showMenu}
                closeMobileMenu={() => {
                  setShowMenu(false);
                }}
              >
                <MenuNav />
              </HeaderNav>
            </AccordionProvider>
          ) : null}
        </div>
      </header>
      <div
        className={classNames('usa-overlay', {
          'is-visible': showMenu,
        })}
      />
    </>
  );
};

GlobalHeader.NavItems = NavItems;
GlobalHeader.Menu = NavAccordion;
GlobalHeader.activeClass = 'usa-current';

export default GlobalHeader;
